import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getDictionaryExamples } from "services/dictionary";
import { ApiRequestError } from "services/errors";
import { logError } from "services/logging";
import { routes } from "pages/routes";
import { useUser } from "contexts/UserContext";
import Dialog from "components/ui/Dialog";
import { GhostButton } from "components/adminUi/Button";
import Button from "components/ui/Button";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import Paywall from "components/Paywall";
import { VStack } from "components/Stack";
import LockIcon from "images/lock.svg?react";
import ForbiddenImg from "images/403.svg?react";
import "./SaveWords.css";
import ApiErrorBanner from "components/ApiErrorBanner";

type SentencesExampleDialogProps = {
  open: boolean;
  word: { word: string; id: string };
  onClose: () => void;
};

export const SentencesExampleDialog = ({
  open,
  word,
  onClose,
}: SentencesExampleDialogProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [examples, setExamples] = useState<string[]>([]);
  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState<unknown>();
  const [showLimitError, setShowLimitError] = useState(false);
  const user = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchExamples = async () => {
    setIsLoading(true);
    setError(null);

    for (let attempts = 0; attempts < 5; attempts++) {
      let response;
      try {
        response = await getDictionaryExamples({ id: word.id, count: 3 });
      } catch (e) {
        if (e instanceof ApiRequestError && e.response?.status === 429) {
          setShowLimitError(true);
          setCount(0);
          setExamples([]);
          setError(null);
          break;
        }

        logError("Got an error during loading examples", e);
        setCount(0);
        setExamples([]);
        setShowLimitError(false);
        setError(e);
        break;
      }
      if (response.noContent) {
        // 204 No Content is a signal by server that it still generates examples.
        // Try again in 3 seconds.
        await new Promise(resolve => setTimeout(resolve, 3000));
      } else {
        // Successfully loaded data, show it.
        setExamples(response.body.list);
        break;
      }
    }
    setIsLoading(false);
  };

  const generateMore = () => {
    if (examples.length === 0) {
      return;
    }

    setCount(count + 1);
    void fetchExamples();
  };

  useEffect(() => {
    void fetchExamples();
    // {reason}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word]);

  const renderSentence = (example: string) => {
    const lowerExample = example.toLowerCase();
    const lowerWord = word.word.toLowerCase();

    const indexes = [];
    let index = lowerExample.indexOf(lowerWord);

    while (index !== -1) {
      indexes.push(index);
      index = lowerExample.indexOf(lowerWord, index + 1);
    }

    const result = [];
    const wordEntries: string[] = [];
    let start = 0;
    for (const i of indexes) {
      result.push(example.slice(start, i));
      wordEntries.push(example.slice(i, i + word.word.length));
      start = i + word.word.length;
    }

    result.push(example.slice(start));
    return result.map((slice, i) => {
      if (i !== result.length - 1) {
        return (
          <>
            {slice}
            <b>{wordEntries[i]}</b>
          </>
        );
      }
      return <>{slice}</>;
    });
  };

  const hdUpgrade = () => {
    navigate(routes.settingsBilling.path());
    onClose();
  };

  const hdOnSuccessfulUpgrade = () => {
    setShowLimitError(false);
    void fetchExamples();
  };

  const generateMoreForPro =
    user.canAccessAiExercises && examples.length > 0 && !showLimitError && !isLoading;
  const generateMoreForBasic = !user.canAccessAiExercises && !isLoading;

  return (
    <Dialog
      className="sentence-examples-dialog"
      open={open}
      onClose={onClose}
      closeOnOverlayClick={false}
      title={t("Dictionary.examplesDialog.title", { word: word.word })}
      secondaryAction={
        (generateMoreForPro || generateMoreForBasic) && (
          <Paywall
            onSuccess={hdOnSuccessfulUpgrade}
            triggerContainerClassName={"upgrade-text"}
            enable={examples.length === 0}
            onUpgrade={hdUpgrade}
          >
            <GhostButton
              className={`more ${examples.length > 0 ? "active" : ""}`}
              onClick={generateMore}
            >
              {examples.length === 0 && <LockIcon />}{" "}
              {t("Dictionary.examplesDialog.generateMore")}
            </GhostButton>
          </Paywall>
        )
      }
      mainAction={
        <Button size="small" variant="primary" className="submit" onClick={onClose}>
          {t("Common.actions.done")}
        </Button>
      }
    >
      <ApiErrorBanner error={error} />
      <div className="content">
        {isLoading && <TalaeraSpinner />}

        {!isLoading &&
          examples.length > 0 &&
          examples.map((example, index) => (
            <p key={index} className="sentence">
              <span>{index + 1 + 3 * count}.</span> {renderSentence(example)}
            </p>
          ))}

        {!isLoading && showLimitError && !user.canAccessAiExercises && (
          <>
            <p className="no-data-description">
              {t("Dictionary.examplesDialog.dailyLimitReached")}
            </p>
            <p className="no-data-description" style={{ marginBottom: 0 }}>
              <Trans
                i18nKey={"Dictionary.examplesDialog.dailyLimitAction"}
                values={{ action: t("Common.actions.upgradeToPro") }}
                components={{
                  action: (
                    <Paywall
                      onSuccess={hdUpgrade}
                      triggerContainerClassName={"upgrade-text"}
                      enable
                      onUpgrade={hdUpgrade}
                    >
                      <u className={"upgrade-to-pro"} />
                    </Paywall>
                  ),
                }}
              />
            </p>
          </>
        )}

        {!isLoading && showLimitError && user.canAccessAiExercises && (
          <VStack align="center" className="limit-error">
            <ForbiddenImg />
            <h3>{t("Dictionary.examplesDialog.dailyLimitTitle")}</h3>
            <p>{t("Dictionary.examplesDialog.dailyLimitHint")}</p>
          </VStack>
        )}
      </div>
    </Dialog>
  );
};
